import { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

import styles from './RecoverBookingReferenceModal.module.css';

type RecoverBookingReferenceModalProps = {
  isOpen: boolean;
  email: string;
  error: string | null;
  onClose: () => void;
  onEmailChange: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
};

export const RecoverBookingReferenceModal = ({
  isOpen,
  email,
  error,
  onClose,
  onEmailChange,
  onSubmit,
}: RecoverBookingReferenceModalProps) => {
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  if (!isOpen) return null;

  const inputId = 'recover-booking-email';
  const errorId = 'recover-booking-error';

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="recover-booking-title"
      aria-describedby={error ? errorId : undefined}
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 id="recover-booking-title" className={styles.title}>
            Lost your booking number?
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <p className={styles.description}>
          Fill your email you used to book your transfer
        </p>
        <form
          className={styles.form}
          onSubmit={(e) => {
            onSubmit(e);
          }}
          noValidate
        >
          <div className={styles.field}>
            <input
              id={inputId}
              className={styles.input}
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => {
                onEmailChange(e.target.value);
              }}
              placeholder=" "
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
            />
            <label className={styles.label} htmlFor={inputId}>
              Email<span className={styles.asterisk}>*</span>
            </label>
          </div>
          {error && (
            <p id={errorId} className={styles.errorBox} role="alert">
              {error}
            </p>
          )}
          <button className={styles.submit} type="submit">
            Recover booking reference
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};
